import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  specialty?: string;
  employee_id?: string;
  is_active: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissions: string[];
  roles: string[];
}

// Restore cached user from localStorage for instant refresh
const cachedUser = (() => {
  try {
    const raw = localStorage.getItem('auth_user');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
})();

const cachedPermissions = (() => {
  try {
    const raw = localStorage.getItem('auth_permissions');
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
})();

const cachedRoles = (() => {
  try {
    const raw = localStorage.getItem('auth_roles');
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
})();

const hasToken = !!localStorage.getItem('auth_token');

const initialState: AuthState = {
  user: hasToken ? cachedUser : null,
  token: localStorage.getItem('auth_token'),
  isAuthenticated: hasToken && !!cachedUser, // Instantly authenticated if we have cached data
  isLoading: hasToken && !cachedUser, // Only show loading if token exists but no cached user
  permissions: hasToken ? cachedPermissions : [],
  roles: hasToken ? cachedRoles : [],
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.isLoading = true;
    },
    loginSuccess: (state, action: PayloadAction<{
      user: User;
      token: string;
      permissions: string[];
      roles: string[];
    }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.permissions = action.payload.permissions;
      state.roles = action.payload.roles;
      
      // Store token and user data in localStorage
      localStorage.setItem('auth_token', action.payload.token);
      localStorage.setItem('auth_user', JSON.stringify(action.payload.user));
      localStorage.setItem('auth_permissions', JSON.stringify(action.payload.permissions));
      localStorage.setItem('auth_roles', JSON.stringify(action.payload.roles));
    },
    loginFailure: (state, _action: PayloadAction<string>) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.permissions = [];
      state.roles = [];
      
      // Remove all auth data from localStorage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_permissions');
      localStorage.removeItem('auth_roles');
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.permissions = [];
      state.roles = [];
      
      // Remove all auth data from localStorage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_permissions');
      localStorage.removeItem('auth_roles');
    },
    setAuthLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    checkAuthSuccess: (state, action: PayloadAction<{
      user: User;
      permissions: string[];
      roles: string[];
    }>) => {
      state.user = action.payload.user;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.permissions = action.payload.permissions;
      state.roles = action.payload.roles;
      
      // Update cached user data in localStorage
      localStorage.setItem('auth_user', JSON.stringify(action.payload.user));
      localStorage.setItem('auth_permissions', JSON.stringify(action.payload.permissions));
      localStorage.setItem('auth_roles', JSON.stringify(action.payload.roles));
    },
    checkAuthFailure: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.permissions = [];
      state.roles = [];
      
      // Remove all auth data from localStorage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_permissions');
      localStorage.removeItem('auth_roles');
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  setAuthLoading,
  updateUser,
  checkAuthSuccess,
  checkAuthFailure,
} = authSlice.actions;

// Selectors
export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectUserRole = (state: { auth: AuthState }) => state.auth.user?.role;
export const selectUserPermissions = (state: { auth: AuthState }) => state.auth.permissions;
export const selectUserRoles = (state: { auth: AuthState }) => state.auth.roles;

// Deprecated - use selectUserPermissions
export const selectUserAbilities = (state: { auth: AuthState }) => state.auth.permissions;

// Helper function to check user permissions
export const hasPermission = (permissions: string[], requiredPermission: string): boolean => {
  return permissions.includes('*') || permissions.includes(requiredPermission);
};

// Deprecated - use hasPermission
export const hasAbility = (abilities: string[], requiredAbility: string): boolean => {
  return hasPermission(abilities, requiredAbility);
};

// Helper function to check user roles
export const hasRole = (roles: string[], requiredRoles: string[]): boolean => {
  return requiredRoles.some(role => roles.includes(role));
};

export default authSlice.reducer;